/**
 * Signed-in product type scale. Import these class fragments instead of
 * hand-typing `text-*` / `text-[Npx]` so size, weight, color, and tracking
 * stay aligned across chrome, catalogs, and chat.
 *
 * Caption and kicker stay at default tracking — wide letter-spacing makes
 * 11px read as large as body. Roles: `docs/architecture/product-typography.md`.
 *
 * `cn()` must treat `text-caption` as a font-size (`@brew/ui/cn`) or
 * compact chrome that passes `text-caption` on top of `text-sm` primitives
 * still renders at 14px.
 */
export const TYPE = {
  display:
    'max-w-3xl font-semibold text-2xl text-foreground leading-tight tracking-tight md:text-4xl',
  /** Settings-style page titles: one quiet step above `heading`, never the display scale. */
  pageTitle: 'font-semibold text-xl text-foreground tracking-tight md:text-2xl',
  title: 'truncate font-semibold text-sm text-foreground tracking-tight',
  heading: 'font-semibold text-base text-foreground tracking-tight',
  section: 'font-semibold text-xs text-foreground tracking-tight',
  body: 'text-sm text-foreground',
  bodyMedium: 'font-medium text-sm text-foreground',
  bodyMuted: 'text-sm text-muted-foreground',
  supporting: 'text-xs text-muted-foreground',
  supportingMedium: 'font-medium text-xs text-muted-foreground',
  caption: 'font-medium text-caption text-muted-foreground',
  kicker: 'font-medium text-caption text-muted-foreground uppercase',
} as const

/**
 * Shared chrome recipes on top of the type scale. Use these instead of
 * one-off `h-7` / `px-2.5` / `font-mono` chips so pills, filter triggers,
 * compact buttons, and dropdown rows stay one size.
 *
 * Buttons and surfaces carry the app radius (`rounded-lg`, 12px). A 24px
 * chip at that radius is a capsule, so chips take the step below it
 * (`rounded-sm`, 8px) and stay rounded rectangles.
 */
export const CHROME = {
  pill: 'flex h-6 w-fit items-center rounded-sm px-2 font-medium text-caption',
  filterPill:
    'flex h-6 w-fit items-center rounded-sm border border-border font-medium text-caption',
  tab: 'flex h-6 min-w-0 items-center gap-1.5 rounded-sm px-2 font-medium text-caption',
  button: 'h-6 gap-1 px-2 font-medium text-caption',
  menuItem: 'text-caption',
} as const
